import { api } from './authService';
import { setCookie, setCookieWithMinutes, getCookie, deleteCookie } from './cookieService';
import { getSelectedOrganizationCookieExpireDays, getOrganizationCacheExpireMinutes } from './cookieConfigService';

const ORGANIZATIONS_COOKIE = 'organizations';
const SELECTED_ORGANIZATION_COOKIE = 'selected_organization';

/** Organization name for FastCube (Genie chat is restricted to this org). */
export const FASTCUBE_ORG = 'FastCube';

/** Organizations that can access the Offer Report page. */
export const OFFER_REPORT_ORG_NAMES = Object.freeze(['FastCube', 'Madrivo']);

/** Separator for dropdown option value "id::name" (avoids collision with names) */
const ORG_OPTION_SEP = '\u001e';

/**
 * Organizations service that manages organizations and selected organization in cookies
 */
export const organizationsService = {
  /**
   * @param {Array<{name: string}>|null|undefined} organizations
   * @returns {boolean}
   */
  hasFastCubeAccess(organizations) {
    return organizations?.some((o) => o.name === FASTCUBE_ORG) ?? false;
  },

  /**
   * @param {Array<{name: string}>|null|undefined} organizations
   * @returns {boolean}
   */
  hasOfferReportAccess(organizations) {
    return (
      organizations?.some((o) => OFFER_REPORT_ORG_NAMES.includes(o.name)) ?? false
    );
  },

  /**
   * @param {Array<{name: string, id: string|number}>|null|undefined} organizations
   * @returns {Array<{name: string, id: string|number}>}
   */
  filterFastCubeOrganizations(organizations) {
    return organizations?.filter((o) => o.name === FASTCUBE_ORG) ?? [];
  },

  /**
   * @param {Array<{name: string, id: string|number}>|null|undefined} organizations
   * @returns {Array<{name: string, id: string|number}>}
   */
  filterOfferReportOrganizations(organizations) {
    return organizations?.filter((o) => OFFER_REPORT_ORG_NAMES.includes(o.name)) ?? [];
  },

  /**
   * Encode org as dropdown option value so both id and name are available on selection.
   * @param {{ id: string|number, name: string }} org
   * @returns {string}
   */
  orgToOptionValue(org) {
    const id = typeof org.id === 'number' ? String(org.id) : org.id;
    return `${id}${ORG_OPTION_SEP}${org.name}`;
  },

  /**
   * Parse dropdown value back to { id, name }. Returns null if not a composite value.
   * @param {string} value
   * @returns {{ id: string, name: string }|null}
   */
  parseOrganizationOptionValue(value) {
    if (value == null || typeof value !== 'string') return null;
    const i = value.indexOf(ORG_OPTION_SEP);
    if (i === -1) return null;
    return { id: value.slice(0, i), name: value.slice(i + ORG_OPTION_SEP.length) };
  },

  /**
   * Get the option value string for the currently selected org (for controlled Select).
   * @param {Array<{id: string|number, name: string}>} organizations
   * @param {string} selectedNameOrId - current selection (name or id depending on page)
   * @param {'name'|'id'} format - whether selectedNameOrId is name or id
   * @returns {string}
   */
  getSelectedOptionValue(organizations, selectedNameOrId, format = 'name') {
    if (!organizations?.length || selectedNameOrId == null || selectedNameOrId === '') return '';
    const org = format === 'id'
      ? organizations.find((o) => String(o.id) === String(selectedNameOrId))
      : organizations.find((o) => o.name === selectedNameOrId);
    return org ? this.orgToOptionValue(org) : '';
  },
  /**
   * Get organizations from cookie or fetch from API
   * @param {boolean} forceRefresh - Force refresh from API even if cached
   * @returns {Promise<Array>} - Array of organizations
   */
  async getOrganizations(forceRefresh = false) {
    // Use cached organizations if present (cookie has short TTL, so existence = fresh)
    if (!forceRefresh) {
      const cachedOrgs = this.getOrganizationsFromCookie();
      if (cachedOrgs) {
        // Re-persist with current short TTL so old long-TTL cookies get migrated
        this.setOrganizationsInCookie(cachedOrgs);
        return cachedOrgs;
      }
    }

    // Fetch from API
    try {
      const res = await api.get('/api/v2/organizations/');
      if (res.status === 200 && res.data) {
        const orgs = res.data;
        // Sort organizations alphabetically by name
        const sortedOrgs = [...orgs].sort((a, b) => a.name.localeCompare(b.name));
        
        // Store in cookie
        this.setOrganizationsInCookie(sortedOrgs);
        
        return sortedOrgs;
      }
      return [];
    } catch (error) {
      console.error('Error fetching organizations:', error);
      // Return cached organizations if available, even if expired
      const cachedOrgs = this.getOrganizationsFromCookie();
      if (cachedOrgs) {
        this.setOrganizationsInCookie(cachedOrgs);
        return cachedOrgs;
      }
      throw error;
    }
  },

  /**
   * Get organizations from cookie.
   * Cookie stores JSON array of tuples [organization_name, organization_id].
   * @returns {Array<{name: string, id: string}>|null} - Array of { name, id } or null if not found
   */
  getOrganizationsFromCookie() {
    const orgsJson = getCookie(ORGANIZATIONS_COOKIE);
    if (!orgsJson) {
      return null;
    }
    try {
      const tuples = JSON.parse(orgsJson);
      if (!Array.isArray(tuples)) return null;
      return tuples.map((t) => ({ name: t[0], id: String(t[1]) }));
    } catch (error) {
      console.error('Error parsing organizations from cookie:', error);
      return null;
    }
  },

  /**
   * Store organizations in cookie as JSON array of tuples [organization_name, organization_id].
   * @param {Array<{id: string|number, name: string}>} organizations - Array of organizations (from API or existing cookie format)
   */
  setOrganizationsInCookie(organizations) {
    try {
      const tuples = organizations.map((o) => [o.name, typeof o.id === 'number' ? o.id : String(o.id)]);
      const minutes = getOrganizationCacheExpireMinutes();
      setCookieWithMinutes(ORGANIZATIONS_COOKIE, JSON.stringify(tuples), minutes);
      deleteCookie('organizations_timestamp');
    } catch (error) {
      console.error('Error storing organizations in cookie:', error);
    }
  },

  /**
   * Get selected organization from cookie.
   * Cookie stores a tuple [organization_name, organization_id].
   * @param {string} format - 'name' or 'id' (default: 'name')
   * @returns {string|null} - Selected organization name or id, or null if not found
   */
  getSelectedOrganization(format = 'name') {
    const raw = getCookie(SELECTED_ORGANIZATION_COOKIE);
    if (!raw) {
      return null;
    }
    try {
      const tuple = JSON.parse(raw);
      if (Array.isArray(tuple) && tuple.length >= 2) {
        return format === 'id' ? String(tuple[1]) : tuple[0];
      }
      // Legacy: cookie was a plain string (name)
      return typeof raw === 'string' ? raw : null;
    } catch {
      return typeof raw === 'string' ? (format === 'id' ? null : raw) : null;
    }
  },

  /**
   * Set selected organization in cookie as tuple [organization_name, organization_id].
   * @param {string} organization - Organization name or id
   * @param {string} format - 'name' or 'id' (default: 'name')
   * @param {Array<{name: string, id: string}>|null} orgsOptional - If caller just fetched orgs, pass them here so we don't rely on cookie (avoids race where cookie isn't set yet)
   */
  setSelectedOrganization(organization, format = 'name', orgsOptional = null) {
    if (!organization) {
      deleteCookie(SELECTED_ORGANIZATION_COOKIE);
      return;
    }

    const orgs = orgsOptional ?? this.getOrganizationsFromCookie();
    const org = orgs?.find(
      (o) =>
        o.name === organization ||
        o.id === organization ||
        String(o.id) === String(organization)
    );
    const name = org ? org.name : format === 'name' ? organization : null;
    const id = org ? String(org.id) : format === 'id' ? String(organization) : null;

    const days = getSelectedOrganizationCookieExpireDays();
    if (name != null && id != null) {
      setCookie(SELECTED_ORGANIZATION_COOKIE, JSON.stringify([name, id]), days);
    } else {
      // Fallback: store single value (will be read as legacy by getSelectedOrganization)
      setCookie(SELECTED_ORGANIZATION_COOKIE, JSON.stringify(organization), days);
    }
  },

  /**
   * Clear organizations and selected organization from cookies
   */
  clearOrganizations() {
    deleteCookie(ORGANIZATIONS_COOKIE);
    deleteCookie(SELECTED_ORGANIZATION_COOKIE);
    deleteCookie('organizations_timestamp');
  },

  /**
   * Drop cached org list only (keeps ``selected_organization``). Used after login so the next read is fresh.
   */
  clearOrganizationsListCookie() {
    deleteCookie(ORGANIZATIONS_COOKIE);
    deleteCookie('organizations_timestamp');
  },

  /**
   * Clear org list cookie and fetch from API (e.g. after login). Does not throw; returns [] on failure.
   * @returns {Promise<Array<{name: string, id: string}>>}
   */
  async refreshOrganizationsAfterLogin() {
    this.clearOrganizationsListCookie();
    try {
      return await this.getOrganizations(true);
    } catch (error) {
      console.error('Error refreshing organizations after login:', error);
      return [];
    }
  },

  /**
   * Refresh organizations from API and update cookie
   * @returns {Promise<Array>} - Array of organizations
   */
  async refreshOrganizations() {
    return this.getOrganizations(true);
  },
};
